import { defineStore } from 'pinia'

import axios from 'axios'

import { getAPIURL } from '@/config'
const API_URL = getAPIURL()

export const useCompanyStore = defineStore({
    id: 'company',

    state: () => ({
        companyList: [],
        company: {
            CompanyId: null,
            CompanyName: null,
            SubscriptionType: null,
            SocketioDeviceURL: null,
            users: [],
            // Mirrors the backend column name. Backend's to_dict() parses
            // the JSON column and returns this as an object, so the
            // frontend never sees a stringified payload here.
            CompanySettings: {}
        },
        loading: false,
        error: null,
        // Retail-coach goals (store_goals table) for the current company.
        goals: [],
        // Durable facts Allen remembers (conversation_memory); active rows only.
        memory: [],
        // Proactive findings (coach_findings) — the Home "what Allen noticed" feed.
        findings: [],
        // Cached coach digest (coach_digest:{cid}:{slot}); null = none yet.
        digest: null,
        // Home overview payload (store_type, kpis, digest, goals, flags).
        homeSummary: null,
    }),

    actions: {
        async fetchCompanyList() {
            try {
                const { data } = await axios.get(`${API_URL}/companies`)
                this.companyList = data
            } catch (error) {
            this.error = error.response?.data?.message || 'Failed to fetch company list'
            throw new Error(this.error)
            }
        },

        async registerCompany(companyData) {
            try {
                const response = await axios.post(`${API_URL}/add_company`, companyData)
                const companyId = response.data.CompanyId
          
                // Optionally: store the created company (e.g. for later use)
                this.company = {
                    ...companyData,
                    CompanyId: companyId
                }
          
                return companyId
            } catch (error) {
                this.error = error.response?.data?.message || 'Company registration failed'
                throw new Error(this.error)
            }
        },

        async fetchCompanyDetails(companyId) {
            this.loading = true
            // If not, fetch from API
            try {
                const response = await axios.get(`${API_URL}/company-details/${companyId}`)
                this.company = response.data
                // Defensive: backend always returns CompanySettings as a parsed
                // object (or {} when NULL), but in case an older payload reaches
                // us with the field missing entirely, ensure we never hand a
                // null/undefined to consumers that expect to read .sector etc.
                if (!this.company.CompanySettings || typeof this.company.CompanySettings !== 'object') {
                    this.company.CompanySettings = {}
                }
            } catch (error) {
                this.error = error.response.data.message
                throw new Error(error.response.data.message)
            } finally {
                this.loading = false
            }
        },

        async updateCompanyUrl(company) {
            try {
                await axios.put(`${API_URL}/api/company/${company.CompanyId}/url`, { SocketioDeviceURL: company.SocketioDeviceURL })
            } catch (error) {
                console.error('Failed to update company URL:', error)
                throw error
            }
        },

        // Merge-then-replace pattern: caller passes a partial settings object,
        // we merge it with current settings (so a caller updating just `sector`
        // doesn't wipe other keys), then PUT the merged object. Mirrors how
        // DetectionZoneSettings updates flow: backend always receives the
        // complete object and replaces the whole column.
        async updateCompanySettings(partialSettings) {
            if (!this.company.CompanyId) {
                throw new Error('updateCompanySettings called without a loaded company')
            }
            const merged = {
                ...(this.company.CompanySettings || {}),
                ...partialSettings,
            }
            try {
                const response = await axios.put(
                    `${API_URL}/api/company/${this.company.CompanyId}/settings`,
                    { CompanySettings: merged }
                )
                // Trust the server's echo so we stay in sync with whatever it
                // actually persisted (also defends against malformed inputs).
                this.company.CompanySettings = response.data?.CompanySettings ?? merged
                return this.company.CompanySettings
            } catch (error) {
                console.error('Failed to update CompanySettings:', error)
                throw error
            }
        },

        // ---- Retail-coach goals (store_goals table, /coach/goals endpoints) ----
        // The company-wide StoreProfile is NOT here — it lives in
        // CompanySettings.coachProfile and is saved via updateCompanySettings.
        async fetchGoals() {
            const { data } = await axios.get(`${API_URL}/coach/goals/`)
            this.goals = data.goals || []
            return this.goals
        },

        async createGoal(goalData, status = 'active') {
            const { data } = await axios.post(`${API_URL}/coach/goals/`, {
                GoalData: goalData,
                Status: status,
            })
            this.goals.unshift(data)   // newest first, matching the GET order
            return data
        },

        async updateGoal(goalId, patch) {
            const { data } = await axios.patch(`${API_URL}/coach/goals/${goalId}/`, patch)
            const idx = this.goals.findIndex(g => g.GoalId === goalId)
            if (idx !== -1) this.goals[idx] = data
            return data
        },

        // ---- Retail-coach durable memory (conversation_memory, /coach/memory) ----
        // Lasting facts Allen has learned about the store — recalled into every
        // chat. The chat itself can also add facts (the remember_fact tool); this
        // is the manager's review/teach/forget surface.
        async fetchCoachMemory() {
            const { data } = await axios.get(`${API_URL}/coach/memory/`)
            this.memory = Array.isArray(data) ? data.filter(m => m.Status === 'active') : []
            return this.memory
        },

        async addCoachMemory(fact) {
            const { data } = await axios.post(`${API_URL}/coach/memory/`, { fact })
            if (data && data.status === 'remembered') await this.fetchCoachMemory()
            return data
        },

        async forgetCoachMemory(memoryId) {
            await axios.delete(`${API_URL}/coach/memory/${memoryId}/`)
            this.memory = this.memory.filter(m => m.MemoryId !== memoryId)
        },

        // ---- Proactive findings (coach_findings, /coach/findings endpoints) ----
        // The nightly "what changed" feed. Dismissing one suppresses its signature
        // so it won't re-appear on the next run.
        async fetchFindings() {
            const { data } = await axios.get(`${API_URL}/coach/findings/`)
            this.findings = Array.isArray(data) ? data : []
            return this.findings
        },

        async dismissFinding(findingId) {
            await axios.post(`${API_URL}/coach/findings/${findingId}/dismiss/`)
            this.findings = this.findings.filter(f => f.FindingId !== findingId)
        },

        // ---- Guidance actions (guidance_actions, /coach/guidance) ----
        // Logged real-world changes; the backend measures each one's before/after
        // effect + € impact. Write is admin-only; read is any authenticated user.
        async fetchGuidanceActions() {
            const { data } = await axios.get(`${API_URL}/coach/guidance/`)
            return Array.isArray(data) ? data : []
        },
        async createGuidanceAction(payload) {
            const { data } = await axios.post(`${API_URL}/coach/guidance/`, payload)
            return data
        },
        async updateGuidanceAction(id, patch) {
            const { data } = await axios.patch(`${API_URL}/coach/guidance/${id}/`, patch)
            return data
        },
        async deleteGuidanceAction(id) {
            await axios.delete(`${API_URL}/coach/guidance/${id}/`)
        },
        async measureGuidanceAction(id) {
            const { data } = await axios.get(`${API_URL}/coach/guidance/${id}/measure/`)
            return data
        },

        // ---- Promotions (promos, /promos) ----
        // A promo is user-supplied ground truth the cameras cannot observe — price,
        // discount, mechanic, which items — measured over three PINNED windows that
        // end. Distinct from a guidance action, whose window slides and grows.
        // Write is admin-only; read is any authenticated user.
        // See docs/reference/promo_measurement.md.
        async fetchPromos({ status = null, zone = null, measure = false } = {}) {
            const params = {}
            if (status) params.status = status
            if (zone) params.zone = zone
            // One request for the whole list INCLUDING each promo's measurement. The
            // server does the same work either way — a measurement is computed, never
            // cached — but the overview used to pay a round trip per promo on top of
            // the list, and on a dozen promos that was most of the wait.
            if (measure) params.measure = 1
            const { data } = await axios.get(`${API_URL}/promos/`, { params })
            return Array.isArray(data) ? data : []
        },
        async createPromo(payload) {
            const { data } = await axios.post(`${API_URL}/promos/`, payload)
            return data
        },
        /**
         * PATCH merges `salesPeriods` per period on the server, because the three
         * till figures become knowable weeks apart — send only what you have.
         */
        async updatePromo(id, patch) {
            const { data } = await axios.patch(`${API_URL}/promos/${id}/`, patch)
            return data
        },
        async deletePromo(id) {
            await axios.delete(`${API_URL}/promos/${id}/`)
        },
        /** The full measurement, computed on request — no precompute, no cache. */
        async measurePromo(id) {
            const { data } = await axios.get(`${API_URL}/promos/${id}/measure/`)
            // The endpoint answers with the promo ROW carrying its measurement
            // (`dict(d, measurement=m)`), not the measurement on its own. Returning
            // the wrapper made every field the pages read — phase, zone, capability,
            // windows — undefined, which does not fail loudly: the detail page drew
            // a hollow shell and the overview filed the promo under "Running",
            // because `undefined !== 'complete'`.
            return (data && typeof data === 'object' && data.measurement)
                ? data.measurement : data
        },
        /**
         * Product zones that can carry a promo: `{eligible, countsOnly}`, countsOnly
         * being the counting-line zones among them (no lingerer split, so no
         * engagement signal — the log form says so instead of showing a 0% stop rate).
         */
        async fetchPromoZones() {
            const { data } = await axios.get(`${API_URL}/promos/zones/`)
            return {
                eligible: Array.isArray(data?.eligible) ? data.eligible : [],
                countsOnly: Array.isArray(data?.countsOnly) ? data.countsOnly : [],
            }
        },
        /** Live feedback on the log form: baseline, what each half can detect, risks. */
        async fetchPromoReadiness(params) {
            const { data } = await axios.get(`${API_URL}/promos/readiness/`, { params })
            return data
        },
        /** {zone: [{promoId, name, start, end}]} for the year-at-a-glance strip. */
        async fetchPromoBands() {
            const { data } = await axios.get(`${API_URL}/promos/bands/`)
            return data && typeof data === 'object' ? data : {}
        },

        // ---- Guidance sales (guidance_sales, /coach/sales) — POS surrogate ----
        async fetchGuidanceSales(category = null) {
            const { data } = await axios.get(`${API_URL}/coach/sales/`, {
                params: category ? { category } : {},
            })
            return Array.isArray(data) ? data : []
        },
        async upsertGuidanceSale(payload) {
            const { data } = await axios.post(`${API_URL}/coach/sales/`, payload)
            return data
        },
        async deleteGuidanceSale(id) {
            await axios.delete(`${API_URL}/coach/sales/${id}/`)
        },

        // Per-zone context-aware recommendations for the Guidance inbox (precomputed).
        async fetchGuidanceRecommendations(slot = 'past_30_days') {
            const { data } = await axios.get(`${API_URL}/coach/recommendations/`, { params: { slot } })
            return data && typeof data === 'object' ? data : {}
        },

        // Measured revenue roll-up for the Today "Value delivered" (PROVE) block —
        // the same figure the Guidance tab headlines. Backend sums impact from
        // GuidanceActions (measured/'good' only). Shape: { run_rate, cumulative, since }
        // (€/mo run-rate, cumulative € banked, ISO date). Returns null when none.
        async fetchGuidanceRollup(slot = 'past_30_days') {
            try {
                const { data } = await axios.get(`${API_URL}/coach/guidance/rollup/`, { params: { slot } })
                return data && typeof data === 'object' && data.run_rate != null ? data : null
            } catch (_) {
                return null
            }
        },

        // Zones announced as NEW in this week's "Allen has N new suggestions" message —
        // Home tags those focus cards "NEW" all week. [] on none / any failure (a tag is
        // never worth breaking the landing for).
        async fetchNewSuggestionZones() {
            try {
                const { data } = await axios.get(`${API_URL}/coach/suggestions/new/`)
                return Array.isArray(data?.zones) ? data.zones : []
            } catch (_) {
                return []
            }
        },

        // Durable dismiss/snooze state for suggestions (company-wide, survives reload).
        async fetchGuidanceSuggestionStates() {
            const { data } = await axios.get(`${API_URL}/coach/suggestion_states/`)
            return data && typeof data === 'object' ? data : {}
        },
        async setGuidanceSuggestionState(idea, state, snoozeDays = null) {
            const { data } = await axios.post(`${API_URL}/coach/suggestion_states/`, { idea, state, snoozeDays })
            return data
        },

        // ---- Structured per-zone context (coachContext, /coach/zone_context) ----
        // GET returns the zone's attribute schema + Allen's derived guesses. Confirms
        // are NOT written here — they ride along in the zone's settings Save (the
        // coachContext key travels in DetectionZoneSettings).
        async fetchZoneContext(zoneId) {
            const { data } = await axios.get(`${API_URL}/coach/zone_context/${zoneId}/`)
            return data
        },
        /**
         * The same schema + guesses for a zone that does not exist yet — the one
         * being drawn right now. The by-id call cannot answer it: there is no id
         * until the flow is saved, which hid the context panel at exactly the moment
         * someone is looking at the camera image and can answer what the zone covers.
         */
        async proposeZoneContext({ type, name, geometry }) {
            const { data } = await axios.get(`${API_URL}/coach/zone_context/propose/`,
                { params: { type, name: name || '', geometry: geometry || undefined } })
            return data
        },

        // ---- Retail-coach digest (cached coach_digest key, /coach/digest) ----
        // Returns the digest payload, or null when none exists yet (HTTP 204:
        // coach disabled for the company, or the nightly run hasn't produced
        // one). The presence of a digest is itself the gate — no flag needed.
        // `style: 'synthesis'` requests the trimmed Today-briefing variant
        // (GET /coach/digest/?style=synthesis); the backend falls back to the full
        // digest when no synthesis is cached. Omit style for the self-contained
        // full digest (HomeOverview). Param is only sent when set, so the full
        // request stays byte-identical to before.
        async fetchDigest(slot = 'past_30_days', { style } = {}) {
            try {
                const params = { slot }
                if (style) params.style = style
                const { data } = await axios.get(`${API_URL}/coach/digest/`, { params })
                this.digest = data && data.digest ? data : null
            } catch (err) {
                this.digest = null
            }
            return this.digest
        },

        // One call for the Home page (store_type, kpis, digest, goals, flags).
        async fetchHomeSummary(slot = 'past_30_days') {
            try {
                const { data } = await axios.get(`${API_URL}/coach/home_summary/`, { params: { slot } })
                this.homeSummary = data
            } catch (err) {
                this.homeSummary = null
            }
            return this.homeSummary
        },

        // ---- Ask Allen: grounded Q&A. POST enqueues a job (the slow model call
        // runs in a backend worker); we then poll the result key until ready. ----
        async askAllen(question, slot = 'past_30_days', history = []) {
            const { data } = await axios.post(`${API_URL}/coach/ask/`, { question, slot, history })
            const jobId = data.job_id
            // poll up to ~2 min (the local model is slow on the prod CPU)
            for (let i = 0; i < 80; i++) {
                await new Promise(r => setTimeout(r, 2000))
                const res = await axios.get(`${API_URL}/coach/ask/result/${jobId}/`)
                const s = res.data.status
                if (s === 'complete') return res.data.answer
                if (s === 'error') throw new Error(res.data.error || 'Allen could not answer that.')
                if (s === 'expired') throw new Error('Allen timed out — please try again.')
            }
            throw new Error('Allen is taking longer than usual — please try again.')
        },

        clearCompanyState() {
            this.company = {
                CompanyId: null,
                CompanyName: null,
                SubscriptionType: null,
                SocketioDeviceURL: null,
                users: [],
                CompanySettings: {}
                // reset other relevant properties
            }
            // Drop EVERY company-scoped cache too. This runs on company switch;
            // without it the next company briefly renders the previous one's
            // findings / digest / summary / goals (even another customer's data)
            // until the refetch lands — a real privacy + correctness bug on Home.
            this.goals = []
            this.memory = []
            this.findings = []
            this.digest = null
            this.homeSummary = null
            //console.log('Company state cleared:', this.company)
            localStorage.removeItem('companyDetails')
        }
    }
})